import cloneDeep from 'lodash-es/cloneDeep';
import moment from 'moment-timezone';

import { ViewingList } from 'inquiries/viewing-list.model';
import { Listing } from 'listing.model';
import { FilterViewModel } from 'clientside/preact/components/inquiries/viewing-list/filter-view-model';
import { VlPropertyFilterParams } from 'inquiries/vl-property-filter-params.model';
import { HOPPLER_TIMEZONE } from '../../constants';

export class ViewingListManager {
  constructor(private viewingList: ViewingList) {}

  copyViewingList(viewingList: ViewingList) {
    return cloneDeep(viewingList);
  }

  addToFinalList(listing: Listing, fromGroup?: string) {
    const vlCopy = this.copyViewingList(this.viewingList);

    if (!vlCopy.finalList.statusKeys) {
      vlCopy.finalList.statusKeys = [];
    }

    // Prevent properties from being added twice
    if (!vlCopy.finalList.keys.includes(listing.propertyKey)) {
      vlCopy.finalList.results.push(listing);
      vlCopy.finalList.statusKeys.push({
        key: listing.propertyKey,
        status: ''
      });
      vlCopy.finalList.keys.push(listing.propertyKey);
    }

    if (fromGroup === 'search' && !vlCopy.search.keys.includes(listing.propertyKey)) {
      vlCopy.search.keys.push(listing.propertyKey);
      vlCopy.search.results.push(listing);
    }

    this.viewingList = vlCopy;
    return vlCopy;
  }

  removeFromFinalList(listing: Listing) {
    const originalVl = this.viewingList;
    const vlCopy = this.copyViewingList(this.viewingList);

    vlCopy.finalList.results = originalVl.finalList.results.filter(
      propertyInList => propertyInList.propertyKey != listing.propertyKey
    );
    vlCopy.finalList.keys = originalVl.finalList.keys.filter(
      key => key != listing.propertyKey
    );
    vlCopy.finalList.statusKeys = originalVl.finalList.statusKeys.filter(
      statusKey => statusKey.key != listing.propertyKey
    );

    this.viewingList = vlCopy;
    return vlCopy;
  }

  updateListingProgressStatus(listing: Listing, progress: string) {
    const vlCopy = this.copyViewingList(this.viewingList);

    const statusKey = vlCopy.finalList.statusKeys.find(sk => sk.key === listing.propertyKey);
    statusKey.status = progress;

    this.viewingList = vlCopy;
    return vlCopy;
  }

  /**
   * Removes fields that the API does not need for saving, making the network
   * payload smaller.
   * 
   * @param viewingList The viewing list to trim down
   */
  trimForAPI(viewingList: ViewingList) {
    const { search, finalList } = viewingList;
    const apiInputCopy: ViewingList = { search, finalList };

    return apiInputCopy;
  }

  prepareFilterForPropertySearch(filter: FilterViewModel) {
    const params: VlPropertyFilterParams = {};
    
    if (filter.uploader && filter.uploader !== 'All') {
      params.source = filter.uploader === 'Direct only' ? 'Direct' : 'Network';
    }
    
    params.search = filter.propertyKey;

    if (filter.location) {
      params.locationId = filter.location.uId;
    }

    params.uploader = filter.uploaderName;
    params.salesDirector = filter.salesDirector;
    params.feStatus = filter.feStatus;

    if (filter.preSale && filter.preSale !== 'All') {
      params.preSale = filter.preSale === 'Pre-selling only' ? '1' : '0';
    }
    
    params.division = filter.division;

    if (filter.categoryLabel) {
      params.category = filter.categoryLabel === 'For rent' ? 'Rent' : 'Sale';
    }

    if (filter.images && filter.images !== 'All') {
      params.images = filter.images === 'With photos' ? '1' : '0';
    }

    Object.keys(filter).filter(
      key => key.startsWith('date')
    ).forEach(key => {
      params[key] = moment(filter[key]).tz(HOPPLER_TIMEZONE).format();
    });

    params.type = filter.propertyType;
    
    // Prevent empty strings/nulls from passing in
    params.minPrice = parseFloat(filter.minPrice) || undefined;
    params.maxPrice = parseFloat(filter.maxPrice) || undefined;
    params.minFloorArea = parseFloat(filter.minFloorArea) || undefined;
    params.maxFloorArea = parseFloat(filter.maxFloorArea) || undefined;

    switch (filter.bedrooms) {
      case 'Studio':
        params.bedrooms = '0';
        break;
      case '5+':
        params.bedrooms = '5';
        break;
      default:
        params.bedrooms = filter.bedrooms;
    }

    if (filter.ownerReceipt && filter.ownerReceipt !== 'All') {
      params.ownerReceipt = filter.ownerReceipt === 'With receipts' ? '1' : '0';
    }

    return params;
  }
}
